import React, { useCallback, useEffect, useState } from "react";
import { YogGoalsSeoMeta } from "./YogGoalsSeoMeta";
import useYogGoalsAnalytics from "./useYogGoalsAnalytics";

import {
  Section,
  Blob,
  Inner,
  PageHeader,
  Eyebrow,
  Title,
  Desc,
  GoalsGrid,
  GoalCard,
  GoalIcon,
  GoalContent,
  GoalTitle,
  GoalDescription,
  GoalArrow,
  TrialRow,
  TrialText,
  TrialTitle,
  TrialDescription,
  TrialButton,
} from "./YogGoals.styles";

const GOALS = [
  {
    id: "stress-anxiety",
    icon: "☁️",
    title: "Stress & Anxiety",
    description:
      "Calm the mind, release tension, and build a more balanced daily routine.",
    keywords: [
      "stress",
      "anxiety",
      "calm",
      "relaxation",
      "meditation",
      "pranayama",
      "mind",
    ],
  },
  {
    id: "fitness-strength",
    icon: "🌿",
    title: "Fitness & Strength",
    description:
      "Improve strength, flexibility, mobility, and overall physical fitness.",
    keywords: [
      "strength",
      "fitness",
      "flexibility",
      "mobility",
      "balance",
      "core",
    ],
  },
  {
    id: "womens-wellness",
    icon: "🌸",
    title: "Women's Wellness",
    description:
      "Yoga practices designed around women's changing needs and wellbeing.",
    keywords: [
      "women",
      "women's",
      "female",
      "menstruation",
      "period",
      "hormone",
    ],
  },
  {
    id: "prenatal-yoga",
    icon: "🤍",
    title: "Prenatal Yoga",
    description:
      "Gentle, supportive practices for a healthy and mindful pregnancy.",
    keywords: [
      "prenatal",
      "pregnancy",
      "pregnant",
      "pregnancy yoga",
    ],
  },
  {
    id: "back-neck-care",
    icon: "🧘",
    title: "Back & Neck Care",
    description:
      "Improve mobility and develop mindful movement for everyday comfort.",
    keywords: [
      "back",
      "neck",
      "spine",
      "shoulder",
      "posture",
      "lower back",
    ],
  },
  {
    id: "beginner-yoga",
    icon: "🌱",
    title: "Beginner Yoga",
    description:
      "Build a strong foundation with guidance that's appropriate for your level.",
    keywords: [
      "beginner",
      "basic",
      "foundation",
      "easy",
      "gentle",
    ],
  },
];

const YogGoals = ({
  onGoalSelect,
  trialHref = "contact",
}) => {
  const {
    trackPageView,
    trackGoalSelect,
    trackTrialClick,
  } = useYogGoalsAnalytics();

  const [activeGoal, setActiveGoal] = useState(null);

  useEffect(() => {
    trackPageView(
      "Find Your Yoga Practice",
      window.location.href
    );
  }, [trackPageView]);

  /**
   * Handle goal selection.
   *
   * If the parent component provides onGoalSelect,
   * we allow the parent to control navigation.
   *
   * Otherwise, we navigate directly to:
   *
   * /yoga-goals/:goalId
   *
   * Example:
   * /yoga-goals/stress-anxiety
   */
  const handleGoalClick = useCallback(
    (goal) => {
      setActiveGoal(goal);

      trackGoalSelect(
        goal,
        GOALS.indexOf(goal)
      );

      // Allow the parent component to handle navigation
      // if it already provides an onGoalSelect callback.
      if (onGoalSelect) {
        onGoalSelect(goal);
        return;
      }

      // Default navigation.
      if (typeof window !== "undefined") {
        const nextPath =
          `/yoga-goals/${encodeURIComponent(goal.id)}`;

        window.location.assign(nextPath);
      }
    },
    [trackGoalSelect, onGoalSelect]
  );

  return (
    <>
      <YogGoalsSeoMeta
        goals={GOALS}
        activeGoal={activeGoal}
      />

      <Section
        id="yoga-goals"
        aria-labelledby="yoga-goals-title"
      >
        <Blob
          className="g1"
          aria-hidden="true"
        />

        <Blob
          className="g2"
          aria-hidden="true"
        />

        <Blob
          className="g3"
          aria-hidden="true"
        />

        <Inner>
          <PageHeader>
            <Eyebrow>
              ✦ FIND YOUR PRACTICE
            </Eyebrow>

            <Title id="yoga-goals-title">
              What Would You Like to Improve?
            </Title>

            <Desc>
              Whether you're looking to reduce stress,
              improve flexibility, build strength, or support
              your overall wellbeing, find a yoga practice that
              fits your goals.
            </Desc>
          </PageHeader>

          <GoalsGrid
            aria-label="Choose your yoga goal"
          >
            {GOALS.map((goal, index) => (
              <GoalCard
                key={goal.id}
                type="button"
                $index={index}
                onClick={() =>
                  handleGoalClick(goal)
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" ||
                    event.key === " "
                  ) {
                    event.preventDefault();
                    handleGoalClick(goal);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`Explore ${goal.title} yoga`}
              >
                <GoalIcon
                  aria-hidden="true"
                >
                  {goal.icon}
                </GoalIcon>

                <GoalContent>
                  <GoalTitle>
                    {goal.title}
                  </GoalTitle>

                  <GoalDescription>
                    {goal.description}
                  </GoalDescription>
                </GoalContent>

                <GoalArrow
                  aria-hidden="true"
                >
                  →
                </GoalArrow>
              </GoalCard>
            ))}
          </GoalsGrid>

          <TrialRow>
            <TrialText>
              <TrialTitle>
                Not sure where to start?
              </TrialTitle>

              <TrialDescription>
                Let us help you find the right practice for
                your goals.
              </TrialDescription>
            </TrialText>

            <TrialButton
              href={trialHref}
              onClick={() =>
                trackTrialClick(activeGoal)
              }
            >
              Book a Free Trial
            </TrialButton>
          </TrialRow>
        </Inner>
      </Section>
    </>
  );
};

YogGoals.displayName = "YogGoals";

export default React.memo(YogGoals);